"use client";

/**
 * Recovers from stale Next.js split chunks (ChunkLoadError) by reloading once.
 * Common after dev cache clears, branch switches, or production deploys while a tab stays open.
 * Complements importWithChunkRecovery() for errors that never reach dynamic() catch handlers.
 */

import {
  CHUNK_RECOVERY_SESSION_KEY,
  isChunkLoadFailure,
} from "@/lib/chunk-load-utils";
import { useEffect } from "react";

export function ChunkLoadRecovery(): null {
  useEffect(() => {
    const clearId = window.setTimeout(() => {
      sessionStorage.removeItem(CHUNK_RECOVERY_SESSION_KEY);
    }, 4000);

    function tryRecover(reason: unknown): boolean {
      if (!isChunkLoadFailure(reason)) return false;
      if (sessionStorage.getItem(CHUNK_RECOVERY_SESSION_KEY) === "1")
        return false;
      sessionStorage.setItem(CHUNK_RECOVERY_SESSION_KEY, "1");
      window.location.reload();
      return true;
    }

    function onUnhandledRejection(event: PromiseRejectionEvent): void {
      if (tryRecover(event.reason)) event.preventDefault();
    }

    function onError(event: ErrorEvent): void {
      const err = event.error;
      if (tryRecover(err ?? event.message)) event.preventDefault();
    }

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onError);
    return () => {
      window.clearTimeout(clearId);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onError);
    };
  }, []);

  return null;
}
